import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { FILE_URL, formatBRL, displayBrand } from "../lib/api";

function fmtCountdown(ms) {
  if (ms < 0) return "00:00";
  // Defensive cap — the reservation TTL on the backend is hard-coded to
  // 15 minutes. If we ever see >16 min remaining it's because `expires_at`
  // lost its UTC marker on the wire and the browser interpreted it as local
  // time (the "194:54" bug). Clamp display so the customer never sees that.
  const MAX_MS = 15 * 60 * 1000;
  const display = Math.min(ms, MAX_MS);
  const totalSec = Math.floor(display / 1000);
  const m = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function ReservationTimer({ expiresAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = useMemo(() => (new Date(expiresAt).getTime() - now), [expiresAt, now]);
  return (
    <p className="text-[12px] text-[#A8A8A8] mt-1" style={{ fontFamily: "'Jost', sans-serif" }}>
      Sua reserva expira em {fmtCountdown(ms)}
    </p>
  );
}

export default function CartDrawer() {
  const { drawerOpen, close, items, subtotal, remove, count } = useCart();
  const nav = useNavigate();

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const goCheckout = () => {
    close();
    nav("/checkout");
  };

  return (
    <>
      <div
        onClick={close}
        className={`fixed inset-0 bg-black/30 z-[100] transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        data-testid="cart-overlay"
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[110] shadow-[-12px_0_30px_rgba(0,0,0,0.06)] flex flex-col transition-transform duration-300 ease-out ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
        role="dialog"
        aria-label="Carrinho"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8E4DF]">
          <h2 className="overline tracking-[0.25em] text-sm">Meu Carrinho</h2>
          <button onClick={close} className="text-[#1A1A1A] hover:text-[#B8A88A]" data-testid="cart-close-btn" aria-label="Fechar carrinho">
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {count === 0 ? (
            <div className="p-10 text-center" data-testid="cart-empty">
              <p className="font-display text-2xl mb-3">Seu carrinho está vazio</p>
              <p className="text-sm text-[#6B6B6B] mb-6">Explore nossa curadoria e adicione peças únicas.</p>
              <Link to="/joias/todos" onClick={close} className="overline text-[#B8A88A] hover:text-[#8C7B5E]">Ver coleção →</Link>
            </div>
          ) : (
            <ul>
              {items.map(({ product, expires_at }) => (
                <li key={product.id} className="flex gap-4 p-6 border-b border-[#F1EDE7]" data-testid={`cart-item-${product.id}`}>
                  <img
                    src={product.images?.[0] ? FILE_URL(product.images[0]) : ""}
                    alt={product.name}
                    className="w-20 h-24 object-cover bg-[#FAF9F7]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base leading-tight">{product.name}</p>
                    {(() => { const b = displayBrand(product); return b ? <p className="text-xs text-[#A8A8A8] mt-1">{b}</p> : null; })()}
                    <p className="text-sm mt-1">{formatBRL(product.price)}</p>
                    <ReservationTimer expiresAt={expires_at} />
                    <button
                      onClick={() => remove(product.id)}
                      className="mt-2 text-xs text-[#6B6B6B] hover:text-[#B85050] inline-flex items-center gap-1"
                      data-testid={`cart-remove-${product.id}`}
                    >
                      <Trash2 size={12}/> Remover
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {count > 0 && (
          <div className="border-t border-[#E8E4DF] p-6 bg-[#FAF9F7]" data-testid="cart-summary">
            <div className="flex justify-between mb-2 overline text-xs">
              <span>Subtotal</span>
              <span>{formatBRL(subtotal)}</span>
            </div>
            <div className="flex justify-between mb-6 text-sm">
              <span className="text-[#6B6B6B]">Frete</span>
              <span className="inline-flex items-center gap-1 text-[#B8A88A]">Gratuito <span aria-hidden>🇧🇷</span></span>
            </div>
            <button onClick={goCheckout} className="w-full bg-[#B8A88A] hover:bg-[#8C7B5E] text-white py-4 transition-colors" style={{ letterSpacing: "0.15em", fontFamily: "'Jost', sans-serif" }} data-testid="cart-checkout-btn">
              IR AO CHECKOUT
            </button>
            <button onClick={close} className="block mx-auto mt-4 text-sm text-[#6B6B6B] hover:text-[#1A1A1A]" data-testid="cart-continue-btn">
              Continuar explorando
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
